import React, { useCallback, useEffect, useRef, useState } from "react";
import { RealtimeClient } from "@openai/realtime-api-beta";
import { WavRecorder } from "src/lib/wavtools/wav_recorder";
import { WavStreamPlayer } from "src/lib/wavtools/wav_stream_player";
import { OPENAI_REALTIME_RELAY_ENDPOINT } from "src/config";
import { getSessionToken } from "src/api/auth";
import { formatMessage } from "src/helpers/format-message";
import { getAmplitude, animateAmplitudeBlob } from "./audio-visualization";
import { Message, MessageSender } from "./types";

// Narrow shapes for the @openai/realtime-api-beta event payloads we actually
// touch. The SDK ships untyped, so these are local descriptors of the fields
// our handlers read; everything else stays as `unknown`.
interface RealtimeEventPayload {
  event: {
    type: string;
    text?: string;
    transcript?: string;
  };
}

interface RealtimeErrorPayload {
  event: { error: unknown };
}

interface ConversationUpdatePayload {
  item: {
    id: string;
    content?: Array<{ type?: string; transcript?: string }>;
  };
  delta?: { audio?: Int16Array };
}

interface UseOpenAIRealtimeOptions {
  orgSlug: string;
  subOrgSlug: string;
  amplitudeRef1: React.RefObject<SVGPathElement>;
  amplitudeRef2: React.RefObject<SVGPathElement>;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

// Owns the OpenAI Realtime voice session: client + recorder + player refs,
// connection lifecycle, and the two incoming-event handlers that append bot
// transcripts to the message list. This is a direct port of the inline voice
// code from Chat.tsx with no behavior changes — including the pre-existing
// response.created / conversation.updated double-append, which is fixed in a
// follow-up commit.
export const useOpenAIRealtime = ({
  orgSlug,
  subOrgSlug,
  amplitudeRef1,
  amplitudeRef2,
  messages,
  setMessages,
}: UseOpenAIRealtimeOptions) => {
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({
      sampleRate: 24000,
    }),
  );

  const wavPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({
      sampleRate: 24000,
    }),
  );

  const openaiClientRef = useRef<RealtimeClient>(
    new RealtimeClient({
      url: `${OPENAI_REALTIME_RELAY_ENDPOINT}/${
        subOrgSlug && subOrgSlug.length > 0 ? subOrgSlug : orgSlug
      }`,
      dangerouslyAllowAPIKeyInBrowser: true,
      apiKey: "",
    }),
  );

  const cancelBlob1Ref = useRef<(() => void) | null>(null);
  const cancelBlob2Ref = useRef<(() => void) | null>(null);

  const [openaiInit, setOpenaiInit] = useState(false);
  const [audioInputDialog, setAudioInputDialog] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);

  // Create realtime client — fetch session token, then swap the ref to a
  // client authenticated with that token.
  useEffect(() => {
    getSessionToken(orgSlug).then((token) => {
      openaiClientRef.current = new RealtimeClient({
        url: `${OPENAI_REALTIME_RELAY_ENDPOINT}/${
          subOrgSlug && subOrgSlug.length > 0 ? subOrgSlug : orgSlug
        }`,
        dangerouslyAllowAPIKeyInBrowser: true,
        apiKey: token,
      });
      setOpenaiInit(true);
    });
  }, [orgSlug, subOrgSlug]);

  // RT OpenAI config — attach handlers once the authenticated client is ready.
  useEffect(() => {
    if (!openaiInit) return;

    const openai = openaiClientRef.current;
    const wavPlayer = wavPlayerRef.current;

    openai.on("realtime.event", (data: RealtimeEventPayload) => {
      if (data.event.type === "response.created") {
        setMessages((prevMessages) => {
          const newMessage = {
            id: prevMessages.length + 1,
            message: data.event.text || "",
            sender: MessageSender.Bot,
            docs: [],
            imagesLoading: false,
            isProcessing: true,
          };

          // Remove the in progress messages
          const filteredMessages = prevMessages.filter((m) => !m.isProcessing);

          return [...filteredMessages, newMessage];
        });
      }
      if (
        data.event.type ===
        "conversation.item.input_audio_transcription.completed"
      ) {
        setMessages((prevMessages) => {
          // Insert a message before the bot message with the transcription
          const before = prevMessages.slice(0, prevMessages.length - 1);
          const after = prevMessages[prevMessages.length - 1];

          const newMessage = {
            id: prevMessages.length + 1,
            message: data.event.transcript || "",
            sender: MessageSender.User,
            docs: [],
            imagesLoading: false,
          };

          return [...before, newMessage, after];
        });
      }
    });
    openai.on("error", (data: RealtimeErrorPayload) => {
      console.error(data.event.error);
    });
    openai.on("conversation.interrupted", async (_data: unknown) => {
      // TODO: Handle audio playback context
      const trackSampleOffset = await wavPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        await openai.cancelResponse(
          trackSampleOffset.trackId,
          trackSampleOffset.offset,
        );
      }
    });
  }, [openaiInit, setMessages]);

  // conversation.updated handler — renders streamed audio transcript into the
  // latest bot message, or appends one if there isn't a bot message yet.
  useEffect(() => {
    const openai = openaiClientRef.current;
    const wavPlayer = wavPlayerRef.current;

    const convoUpdater = ({ item, delta }: ConversationUpdatePayload) => {
      if (delta?.audio) {
        wavPlayer.add16BitPCM(delta.audio, item.id);
      }

      if (item.content && item.content.length > 0) {
        const first = item.content[0];
        if (first.type === "audio" && first.transcript) {
          const transcript = first.transcript;

          // Check if the latest message is processing, if not add a new
          // message and set its content.
          if (messages.length > 0) {
            if (messages[messages.length - 1].sender === MessageSender.Bot) {
              setMessages((prevMessages) => {
                if (prevMessages[prevMessages.length - 1].isProcessing) {
                  prevMessages[prevMessages.length - 1].isProcessing = false;
                }
                prevMessages[prevMessages.length - 1].message =
                  formatMessage(transcript);
                return [...prevMessages];
              });
            } else {
              setMessages((prevMesages) => {
                return [
                  ...prevMesages,
                  {
                    isProcessing: false,
                    message: formatMessage(transcript),
                    sender: MessageSender.Bot,
                    docs: [],
                    imagesLoading: false,
                    id: prevMesages.length + 1,
                  },
                ];
              });
            }
          } else {
            setMessages([
              {
                isProcessing: true,
                message: formatMessage(transcript),
                sender: MessageSender.Bot,
                docs: [],
                imagesLoading: false,
                id: 0,
              },
            ]);
          }
        }
      }
    };

    openai.on("conversation.updated", convoUpdater);

    return () => {
      openai.off("conversation.updated", convoUpdater);
    };
  }, [messages, setMessages]);

  const beginAudio = useCallback(async () => {
    setAudioInputDialog(true);
    if (sessionStarted) {
      return;
    }

    setVoiceLoading(true);
    setSessionStarted(true);

    const client = openaiClientRef.current;
    const wavRecorder = wavRecorderRef.current;

    await wavRecorder.begin();
    await wavPlayerRef.current.connect();
    await client.connect();

    await wavRecorder.record((data) => {
      client.appendInputAudio(data.mono);
    });

    cancelBlob1Ref.current = animateAmplitudeBlob(
      amplitudeRef1,
      () => getAmplitude(wavRecorderRef.current)[0],
    );
    cancelBlob2Ref.current = animateAmplitudeBlob(
      amplitudeRef2,
      () => getAmplitude(wavRecorderRef.current)[1],
    );

    setVoiceLoading(false);
  }, [sessionStarted, amplitudeRef1, amplitudeRef2]);

  const cancelAudio = useCallback(async () => {
    setVoiceLoading(true);
    setAudioInputDialog(false);
    setSessionStarted(false);

    const client = openaiClientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavPlayer = wavPlayerRef.current;

    await wavRecorder.quit();
    client.disconnect();
    wavPlayer.interrupt();

    cancelBlob1Ref.current?.();
    cancelBlob2Ref.current?.();
    cancelBlob1Ref.current = null;
    cancelBlob2Ref.current = null;

    // Delete any message where isProcessing is true
    setMessages((prevMessages) =>
      prevMessages.filter((message) => !message.isProcessing),
    );

    setVoiceLoading(false);
  }, [setMessages]);

  const toggleMute = useCallback(async () => {
    const wavPlayer = wavPlayerRef.current;

    if (wavPlayer.isMuted()) {
      wavPlayer.unmute();
    } else {
      wavPlayer.mute();
    }

    setMuted(wavPlayer.isMuted());
  }, []);

  // Stop amplitude-blob animations on unmount.
  useEffect(() => {
    return () => {
      cancelBlob1Ref.current?.();
      cancelBlob2Ref.current?.();
    };
  }, []);

  return {
    audioInputDialog,
    voiceLoading,
    muted,
    beginAudio,
    cancelAudio,
    toggleMute,
  };
};
